import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { AiChatService, ChatMessage, SuggestedPrompt } from '../../../services/ai-chat.service';
import { AppContextService, AppContext } from '../../../services/app-context.service';
import { UserService } from '../../../services/user.service';
import { HttpTokenService } from '../../../services/http.token.service';
import { Subscription } from 'rxjs';
import { AlertService } from "../../../_alert";

@Component({
    selector: 'app-ai-chat-window',
    templateUrl: './ai-chat-window.component.html',
    styleUrls: ['./ai-chat-window.component.scss']
})
export class AiChatWindowComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
    @ViewChild('messageInput') private messageInput!: ElementRef;

    public isOpen = false;
    public isCollapsed: boolean;
    public isMinimized = false;
    public isLeftPanelOpen = false; // Control left panel visibility
    public messages: ChatMessage[] = [];
    public currentMessage = '';
    public isLoading = false;
    public showDisclaimer = false;
    public disclaimerAccepted = false;
    public currentContext!: AppContext;
    public contextDisplay = '';

    private subscriptions: Subscription[] = [];
    private shouldScrollToBottom = false;
    private readonly MAX_MESSAGE_LENGTH = 1000;

    constructor(
        public aiChatService: AiChatService,
        public appContextService: AppContextService,
        public userService: UserService,
        private tokenService: HttpTokenService,
        public alertService: AlertService
    ) { }

    ngOnInit(): void {
        // Listen for chat toggle events
        window.addEventListener('toggleChat', this.handleToggleChat.bind(this));

        // Subscribe to messages
        const messagesSub = this.aiChatService.messages$.subscribe(messages => {
            this.messages = messages;
            this.shouldScrollToBottom = true;
        });
        this.subscriptions.push(messagesSub);

        // Subscribe to loading state
        const loadingSub = this.aiChatService.loading$.subscribe(loading => {
            this.isLoading = loading;
            if (loading) {
                this.shouldScrollToBottom = true;
            }
        });
        this.subscriptions.push(loadingSub);

        // Subscribe to context changes
        const contextSub = this.appContextService.context$.subscribe(context => {
            console.log('💬 Chat window received context update:', {
                patient_id: context.patient_id,
                program_id: context.program_id,
                screen_name: context.screen_name,
                active_tab: context.active_tab
            });
            this.currentContext = context;
            this.updateContextDisplay(context);
        });
        this.subscriptions.push(contextSub);

        // Subscribe to logout events (manual logout or session timeout)
        const logoutSub = this.tokenService.logoutObserver.subscribe(() => {
            console.log('🔒 User logged out - closing AI chat window');
            // Close the chat window
            this.isOpen = false;
            this.isMinimized = false;
            // Clear chat history on logout for privacy
            this.aiChatService.clearHistory();
        });
        this.subscriptions.push(logoutSub);

        // Check if disclaimer was already accepted
        this.disclaimerAccepted = localStorage.getItem('aiChatDisclaimerAccepted') === 'true';
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    ngOnDestroy(): void {
        window.removeEventListener('toggleChat', this.handleToggleChat.bind(this));
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Handle toggle chat event from button
     */
    private handleToggleChat(event: any): void {
        const shouldOpen = event.detail;

        /*   if (shouldOpen && !this.disclaimerAccepted) {
               this.showDisclaimer = true;
           }*/

        this.isOpen = shouldOpen;
        this.isMinimized = false;

        if (this.isOpen) {
            // Scroll to bottom after a short delay to ensure DOM is rendered
            setTimeout(() => {
                this.shouldScrollToBottom = true;
            }, 50);

            // Focus on input after opening
            setTimeout(() => {
                if (this.messageInput) {
                    this.messageInput.nativeElement.focus();
                }
            }, 150);
        }
    }

    /**
     * Update context display for header
     */
    private updateContextDisplay(context: AppContext): void {
        if (context.patient_id && context.program_id) {
            this.contextDisplay = `Patient #${context.patient_id} - Program #${context.program_id}`;
        } else if (context.patient_id) {
            this.contextDisplay = `Patient #${context.patient_id}`;
        } else if (context.screen_name) {
            this.contextDisplay = this.formatScreenName(context.screen_name);
        } else {
            this.contextDisplay = 'General Assistance';
        }
    }

    /**
     * Format screen name for display
     */
    private formatScreenName(screenName: string): string {
        return screenName
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Accept healthcare disclaimer
     */
    public acceptDisclaimer(): void {
        localStorage.setItem('aiChatDisclaimerAccepted', 'true');
        this.disclaimerAccepted = true;
        this.showDisclaimer = false;
    }

    /**
     * Close disclaimer modal (cancel)
     */
    public closeDisclaimer(): void {
        this.showDisclaimer = false;
        this.isOpen = false;
        // Trigger close event
        const event = new CustomEvent('toggleChat', { detail: false });
        window.dispatchEvent(event);
    }

    /**
     * Send message to AI
     */
    public sendMessage(): void {
        if (!this.currentMessage.trim() || this.isLoading) {
            return;
        }

        this.aiChatService.sendMessage(this.currentMessage);
        this.currentMessage = '';
    }

    /**
     * Handle enter key press in input
     */
    public onKeyPress(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    /**
     * Use a suggested prompt - automatically sends the message with task identifier
     */
    public useSuggestedPrompt(prompt: SuggestedPrompt): void {
        console.log('✨ Suggested prompt clicked:', prompt);

        // Automatically send the message with the task identifier
        this.aiChatService.sendMessage(prompt.text, prompt.task);

        // Clear the input field
        this.currentMessage = '';
    }

    /**
     * Minimize chat window
     */
    public minimize(): void {
        this.isMinimized = !this.isMinimized;
    }

    /**
     * Close chat window
     */
    public close(): void {
        this.isOpen = false;
        this.isMinimized = false;
        // Trigger close event
        const event = new CustomEvent('toggleChat', { detail: false });
        window.dispatchEvent(event);
    }

    /**
     * Toggle left panel visibility
     */
    public toggleLeftPanel(): void {
        this.isLeftPanelOpen = !this.isLeftPanelOpen;
    }

    /**
     * Clear chat history
     */
    public clearHistory(): void {
        this.aiChatService.clearHistory();
        /*  if (confirm('Are you sure you want to clear the chat history?')) {
            this.aiChatService.clearHistory();
          }*/
    }

    /**
     * Copy message content to clipboard
     * @param message - The message to copy
     */
    public copyMessage(message: ChatMessage): void {
        const textToCopy = message.content;

        // Use the Clipboard API if available
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(textToCopy).then(() => {
                console.log('✅ Message copied to clipboard');
                // Optional: Show a temporary success message or toast
                this.alertService.success('Message copied to clipboard!', this.alertService.options);
            }).catch(err => {
                console.error('❌ Failed to copy message:', err);
                // Fallback to older method
                this.fallbackCopyToClipboard(textToCopy);
            });
        } else {
            // Fallback for older browsers
            this.fallbackCopyToClipboard(textToCopy);
        }
    }

    /**
     * Fallback copy method for older browsers
     * @param text - Text to copy
     */
    private fallbackCopyToClipboard(text: string): void {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                console.log('✅ Message copied to clipboard (fallback)');
                this.alertService.success('Message copied to clipboard!', this.alertService.options);
            } else {
                console.error('❌ Failed to copy message (fallback)');
                this.alertService.error('Failed to copy message. Please try selecting and copying manually.', this.alertService.options);
            }
        } catch (err) {
            console.error('❌ Fallback copy error:', err);
            this.alertService.error('Failed to copy message. Please try selecting and copying manually.', this.alertService.options);
        }

        document.body.removeChild(textArea);
    }

    /**
     * Scroll messages to bottom
     */
    private scrollToBottom(): void {
        try {
            if (this.messagesContainer) {
                this.messagesContainer.nativeElement.scrollTop =
                    this.messagesContainer.nativeElement.scrollHeight;
            }
        } catch (err) {
            console.error('Error scrolling to bottom:', err);
        }
    }

    /**
     * Get character count status
     */
    public get characterCount(): number {
        return this.currentMessage.length;
    }

    /**
     * Check if message exceeds max length
     */
    public get isMessageTooLong(): boolean {
        return this.currentMessage.length > this.MAX_MESSAGE_LENGTH;
    }

    /**
     * Format timestamp for display
     */
    public formatTimestamp(date: Date): string {
        const now = new Date();
        const messageDate = new Date(date);
        const diffInMinutes = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60);
            return `${hours}h ago`;
        } else {
            return messageDate.toLocaleDateString();
        }
    }

    /**
     * Parse markdown to HTML with compact, professional formatting
     */
    public parseMarkdown(text: string): string {
        if (!text) return '';

        let parsed = text;

        // Escape HTML first
        parsed = parsed.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        // Code blocks (must be before inline code)
        parsed = parsed.replace(/```([^`]+)```/gs, (match, code) => {
            return `<pre><code>${code.trim()}</code></pre>`;
        });

        // Inline code
        parsed = parsed.replace(/`([^`]+)`/g, '<code>$1</code>');

        // Headers (process in reverse order to avoid conflicts)
        parsed = parsed.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
        parsed = parsed.replace(/^### (.*$)/gim, '<h3>$1</h3>');
        parsed = parsed.replace(/^## (.*$)/gim, '<h2>$1</h2>');
        parsed = parsed.replace(/^# (.*$)/gim, '<h1>$1</h1>');

        // Bold
        parsed = parsed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        // Italic
        parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');

        // Convert both numbered and bulleted list items with proper nesting
        const lines = parsed.split('\n');
        const processedLines: string[] = [];
        let listStack: Array<{ type: 'ul', indent: number }> = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Match numbered list: "1. Item" or "    1. Item"
            const numberedMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);

            // Match bulleted list: "- Item" or "    - Item"
            const bulletMatch = line.match(/^(\s*)-\s+(.+)$/);

            if (numberedMatch) {
                const indent = numberedMatch[1].length;
                const content = numberedMatch[3];

                // Close deeper nested lists
                while (listStack.length > 0 && listStack[listStack.length - 1].indent > indent) {
                    const closingList = listStack.pop();
                    processedLines.push(`</${closingList!.type}>`);
                }

                // If no list or different indent, start new bullet list
                if (listStack.length === 0 || listStack[listStack.length - 1].indent !== indent) {
                    if (listStack.length > 0 && listStack[listStack.length - 1].indent === indent) {
                        const oldList = listStack.pop();
                        processedLines.push(`</${oldList!.type}>`);
                    }
                    processedLines.push('<ul>');
                    listStack.push({ type: 'ul', indent: indent });
                }

                processedLines.push(`<li>${content}</li>`);

            } else if (bulletMatch) {
                const indent = bulletMatch[1].length;
                const content = bulletMatch[2];

                // Close deeper nested lists
                while (listStack.length > 0 && listStack[listStack.length - 1].indent > indent) {
                    const closingList = listStack.pop();
                    processedLines.push(`</${closingList!.type}>`);
                }

                // If no list or different indent, start new bullet list
                if (listStack.length === 0 || listStack[listStack.length - 1].indent !== indent) {
                    if (listStack.length > 0 && listStack[listStack.length - 1].indent === indent) {
                        const oldList = listStack.pop();
                        processedLines.push(`</${oldList!.type}>`);
                    }
                    processedLines.push('<ul>');
                    listStack.push({ type: 'ul', indent: indent });
                }

                processedLines.push(`<li>${content}</li>`);
            } else {
                // Close all open lists when we hit non-list content
                while (listStack.length > 0) {
                    const list = listStack.pop();
                    processedLines.push(`</${list!.type}>`);
                }
                processedLines.push(line);
            }
        }

        // Close any remaining open lists
        while (listStack.length > 0) {
            const list = listStack.pop();
            processedLines.push(`</${list!.type}>`);
        }

        parsed = processedLines.join('\n');

        // Convert double line breaks to paragraph breaks (but not inside lists/headers)
        parsed = parsed.replace(/\n\n+/g, '</p><p>');
        parsed = '<p>' + parsed + '</p>';

        // Clean up empty paragraphs
        parsed = parsed.replace(/<p>\s*<\/p>/g, '');
        parsed = parsed.replace(/<p>\s*<(h[1-6]|ul|pre)/g, '<$1');
        parsed = parsed.replace(/<\/(h[1-6]|ul|pre)>\s*<\/p>/g, '</$1>');

        // Convert single line breaks to minimal spacing (only between different elements)
        parsed = parsed.replace(/\n(?!<)/g, '<br>');

        // Remove excessive breaks around block elements
        parsed = parsed.replace(/<br>\s*<(h[1-6]|ul|li|p|\/ul|\/li|\/p)/g, '<$1');
        parsed = parsed.replace(/<\/(h[1-6]|ul|li|p)>\s*<br>/g, '</$1>');

        // Final cleanup
        parsed = parsed.replace(/<p><br>/g, '<p>');
        parsed = parsed.replace(/<br><\/p>/g, '</p>');

        return parsed;
    }
}
